import React from 'react';

interface StatCardProps {
  label: string;
  value: string;
  /** Optional one-line clarifier rendered under the value in muted ink-
   *  mute. Use for short methodology / unit notes that should be visible
   *  inline rather than hidden behind the (?) tooltip — e.g. "Includes
   *  ~18% coarse-root mass" on the Total Tree Biomass card. */
  subtitle?: string;
  info?: string;
  // 'bottom' renders the tooltip below the card (default). Switch to 'top' for
  // cards in the last row of a grid so the tooltip isn't clipped by the parent
  // panel's overflow-y scroll container.
  tooltipPlacement?: 'top' | 'bottom';
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subtitle,
  info,
  tooltipPlacement = 'bottom',
}) => {
  const tooltipPositionClass =
    tooltipPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1';

  return (
    // Relative so the tooltip positions against the card. Tooltip is a sibling
    // of the icon button and uses `peer-hover` / `peer-focus` so it doesn't
    // inherit the icon's small bounding box — that lets the tooltip span the
    // full card width (left-0 right-0) instead of overflowing off-panel as it
    // did when anchored to the icon.
    <div className="relative bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
      {info && (
        <>
          <button
            type="button"
            aria-label={`About ${label}`}
            className="peer absolute top-2 right-2 text-neutral-500 hover:text-figma-lime focus:text-figma-lime focus:outline-none transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <div
            role="tooltip"
            className={`pointer-events-none absolute ${tooltipPositionClass} left-0 right-0 z-50 invisible opacity-0 peer-hover:visible peer-hover:opacity-100 peer-focus:visible peer-focus:opacity-100 bg-figma-panel border border-figma-lime/40 rounded-figma-panel p-3 shadow-figma-card text-xs font-roboto text-neutral-300 leading-relaxed transition-opacity`}
          >
            {info}
          </div>
        </>
      )}
      <p className="text-body-sm font-roboto text-neutral-400 mb-1">{label}</p>
      <p className="text-xl font-roboto-mono font-bold text-figma-lime">{value}</p>
      {subtitle && (
        <p className="mt-1 text-[11px] font-roboto text-figma-ink-mute leading-snug">
          {subtitle}
        </p>
      )}
    </div>
  );
};
